import { useMutation } from '@tanstack/react-query'
import { signIn } from 'next-auth/react'
import { toast } from 'sonner'

import { resolveHomeRoute } from '@/lib/auth/home-route'
import { type LocalSignInSchema, type RegisterSchema } from '@/models/auth/auth'
import type { UserOnboardingResponse } from '@/models/onboarding/onboarding'
import { type ZUserType } from '@/models'
import { proxyService } from '@/services/proxy/proxyService'

type AuthRedirectOptions = {
  returnTo?: string
}

async function finishSignInRedirect(returnTo?: string, options?: AuthRedirectOptions) {
  const [, onboarding] = await Promise.all([
    proxyService.auth.get<{ user: ZUserType }>('/users/me'),
    proxyService.auth.get<UserOnboardingResponse>('/users/me/onboarding'),
  ])

  window.location.href = resolveHomeRoute({
    onboarding: onboarding.data,
    requestedRedirect: returnTo ?? options?.returnTo,
  })
}

/**
 * Exchange the browser's Kratos session (set by an email code / magic link, see
 * `@/lib/auth/kratos-code-login`) for a NextAuth session, then route home.
 */
export const useLogin = (options?: AuthRedirectOptions) => {
  return useMutation({
    mutationFn: async (returnTo?: string) => {
      toast.loading('Signing in...', {
        id: 'signingIn',
      })

      const result = await signIn('kratos', { redirect: false })

      if (result?.error) {
        throw new Error(result.error)
      }

      await finishSignInRedirect(returnTo, options)

      return result
    },
    onSuccess: () => {
      toast.dismiss('signingIn')
      toast.success('Signed in successfully')
    },
    onError: () => {
      toast.dismiss('signingIn')
      toast.error('Failed to sign in')
    },
  })
}

/** Strapi `/auth/local` via NextAuth `credentials` (legacy email + password accounts). */
export const useLocalLogin = (options?: AuthRedirectOptions) => {
  return useMutation({
    mutationFn: async (data: LocalSignInSchema) => {
      toast.loading('Signing in...', { id: 'signingIn' })

      const result = await signIn('credentials', {
        email: data.email,
        password: data.password,
        redirect: false,
      })

      if (result?.error) {
        throw new Error('invalid_credentials')
      }

      await finishSignInRedirect(options?.returnTo, options)

      return result
    },
    onSuccess: () => {
      toast.dismiss('signingIn')
      toast.success('Signed in successfully')
    },
    onError: () => {
      toast.dismiss('signingIn')
      toast.error('Failed to sign in')
    },
  })
}

export const useRegister = (options?: AuthRedirectOptions) => {
  return useMutation({
    mutationFn: async (data: RegisterSchema) => {
      toast.loading('Registering...', {
        id: 'register',
      })

      const res = await proxyService.post<{ user: ZUserType }>('/auth/local/register', data)
      if (!res.user) {
        throw new Error('Failed to register')
      }

      const result = await signIn('credentials', {
        email: data.email,
        password: data.password,
        redirect: false,
      })

      if (result?.error) {
        throw new Error(result.error)
      }

      const onboarding = await proxyService.auth.get<UserOnboardingResponse>(
        '/users/me/onboarding',
      )

      window.location.href = resolveHomeRoute({
        onboarding: onboarding.data,
        requestedRedirect: options?.returnTo,
      })

      return res
    },
    onSuccess: () => {
      toast.dismiss('register')
      toast.success('Registered successfully')
    },
    onError: () => {
      toast.dismiss('register')
      toast.error('Failed to register')
    },
  })
}
